import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const formatNumber = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-IN").format(num);
};



const StatCard = ({ title, value, sub }) => (
  <div className="col-12 col-sm-6 col-lg-3" style={{ marginBottom: 12 }}>
    <div className="card" style={{ height: "100%" }}>
      <div className="card-body" style={{ padding: 14 }}>
        <div className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
          {title}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6 }}>{value}</div>
        {sub ? (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

const TenantReport = () => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(() => localStorage.getItem("userid"), []);
  const isOwner = useMemo(() => {
    try {
      return String(localStorage.getItem("role") || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "") === "OWNER";
    } catch {
      return false;
    }
  }, []);
  const isAdmin = useMemo(() => {
  return String(localStorage.getItem("role") || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "") === "ADMIN";
}, []);

  const [tenants, setTenants] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Actual search query sent to API
  const [roleFilter, setRoleFilter] = useState("OWNER");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("all"); 
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 1
  });

  const getId = (x) => x?._id || x?.id || x?.userid;

  const fetchTenants = async (page = 1, limit = 25, search = "", role = "") => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    let didIncrement = false;
    try {
      const payload = isOwner
        ? {
            action: "owner.getTenants",
            userid: userId,
            page,
            limit,
            isActive: true,
            isVerified: true,
            sortBy: "createdAt",
            sortOrder: "desc"
          }
        : {
            action: "admin.getTenants",
            userId,
            page,
            limit,
          };

      if (search && search.trim()) {
        payload.search = search.trim();
      }
      if (!isOwner && role) {
        payload.role = role;
      }

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" && Array.isArray(data?.tenants)) {
        setTenants(data.tenants);
        setPagination(data.pagination || {
          page,
          limit,
          total: data.tenants.length,
          pages: 1
        });
      } else {
        toast.error(data?.message || "Failed to load tenants");
        setTenants([]);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
      setTenants([]);
    } finally {
      if (didIncrement) loaderDecrement();
    }
  };

  useEffect(() => {
    fetchTenants(pagination.page, pagination.limit, searchQuery, isOwner ? "" : "OWNER");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadReport = async () => {
    if (!isAdmin) {
      toast.error("Only Admin can download the report.");
      return;
    }

    if (exportMode === "date") {
      if (!fromDate || !toDate) {
        toast.error("Select both From date and To date.");
        return;
      }
      if (fromDate > toDate) {
        toast.error("From date must be before or equal to To date.");
        return;
      }
    }

    let didIncrement = false;
    try {
      setShowExportModal(false);
      loaderIncrement();
      didIncrement = true;

      const payload = { action: "admin.exportOwnerExcel", userId };
      if (exportMode === "date") {
        payload.fromDate = fromDate;
        payload.toDate = toDate;
      }

      const response = await axios.post(API_BASE_URL, payload);
      const data = response?.data;

      if (data?.status !== "success" || !data?.file) {
        toast.error(data?.message || "Failed to download report.");
        return;
      }

      const binary = window.atob(data.file);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        exportMode === "date"
          ? `Owner_Report_${fromDate}_to_${toDate}.xlsx`
          : "Owner_Report_All.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully.");
      setShowExportModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to download report.");
    } finally {
      if (didIncrement) loaderDecrement();
    }
  };

  const applySearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    fetchTenants(1, pagination.limit, searchInput, roleFilter);
  };

  // Handle search input change (for real-time feedback)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    
    // If search is cleared, fetch all tenants
    if (!value.trim() && searchQuery) {
      setSearchQuery("");
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTenants(1, pagination.limit, "", roleFilter);
    }
  };

  const selectRole = (role) => {
    if (role === roleFilter) return;
    setRoleFilter(role);
    setSearchInput("");
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchTenants(1, pagination.limit, "", role);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchTenants(newPage, pagination.limit, searchQuery, roleFilter);
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, page: 1, limit: newLimit }));
    fetchTenants(1, newLimit, searchQuery, roleFilter);
  };

  const handleView = (tenant) => {
    const tid = getId(tenant);
    if (!tid) {
      toast.error("Tenant id missing for this row.");
      return;
    }
    navigate(`/tenant-report/view/${encodeURIComponent(String(tid))}`);
  };

  // const handleEdit = (tenant) => {
  //   const tid = getId(tenant);
  //   if (!tid) {
  //     toast.error("Tenant id missing for this row.");
  //     return;
  //   }
  //   navigate(`/tenant-report/edit/${encodeURIComponent(String(tid))}`);
  // };

  // const handleDelete = (tenant) => {
  //   if (window.confirm(`Are you sure you want to delete ${tenant.name}?`)) {
  //     toast.info(`Deleting ${tenant.name}`);
  //     // Add delete logic here
  //   }
  // };

  // Smart pagination logic
  const generatePageNumbers = () => {
    const current = pagination.page;
    const total = pagination.pages;
    const delta = 2; // Number of pages to show around current page
    
    if (total <= 7) {
      // If total pages <= 7, show all pages
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    
    const pages = [];
    
    // Always show first page
    pages.push(1);
    
    // Calculate start and end of middle section
    let start = Math.max(2, current - delta);
    let end = Math.min(total - 1, current + delta);
    
    // Adjust if we're near the beginning
    if (current <= delta + 2) {
      end = Math.min(total - 1, 5);
    }
    
    // Adjust if we're near the end
    if (current >= total - delta - 1) {
      start = Math.max(2, total - 4);
    }
    
    // Add ellipsis after first page if needed
    if (start > 2) {
      pages.push('...');
    }
    
    // Add middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    // Add ellipsis before last page if needed
    if (end < total - 1) {
      pages.push('...');
    }
    
    // Always show last page (if more than 1 page)
    if (total > 1) {
      pages.push(total);
    }
    
    return pages;
  };

  const pageNumbers = generatePageNumbers();
  const listingOwners = !isOwner && roleFilter === "OWNER";
  const showOwnerColumns = !listingOwners; 
  const showCorpusColumns = listingOwners; 
  const visibleTenants = isOwner || !roleFilter
    ? tenants
    : tenants.filter((tenant) => String(tenant?.role || "").toUpperCase() === roleFilter);
  const apiHonorsRole =
    isOwner ||
    !roleFilter ||
    tenants.every((tenant) => String(tenant?.role || "").toUpperCase() === roleFilter);
  const totalTenants = apiHonorsRole
    ? pagination.total || visibleTenants.length
    : visibleTenants.length;
  const roleLabel = (role) => {
    const value = String(role || "").toUpperCase();
    if (value === "OWNER") return "Owner";
    if (value === "TENANT") return "Tenant";
    return role || "-";
  };

  return (
    <div className="employee-form tenant-report">
      <div className="content-header">
        <div className="container-fluid">
           <div className="d-flex flex-wrap justify-content-between align-items-end" style={{ gap: 12 }}>
            <div>
              <h1 className="m-0 txt_lft">
                {listingOwners ? "Manage Owners" : "Manage Tenants"}
              </h1>
              <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>
                {listingOwners
                  ? "View, edit, and manage owner information and details"
                  : "View, edit, and manage tenant information and details"}
              </p>
            </div>
            {isOwner ? null : (
              <ul className="nav nav-tabs" role="tablist" style={{ borderBottom: "none", marginBottom: 0 }}>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link${roleFilter === "OWNER" ? " active" : ""}`}
                    role="tab"
                    aria-selected={roleFilter === "OWNER"}
                    onClick={() => selectRole("OWNER")}
                  >
                    Owner
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link${roleFilter === "TENANT" ? " active" : ""}`}
                    role="tab"
                    aria-selected={roleFilter === "TENANT"}
                    onClick={() => selectRole("TENANT")}
                  >
                    Tenant
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
                  <div className="row">
            <StatCard
              title={listingOwners ? "Total Owners" : "Total Tenants"}
              value={formatNumber(totalTenants)}
            />
            {isAdmin && listingOwners ? (
              <div
                className="col d-flex justify-content-end align-items-start"
                style={{ marginBottom: 12 }}
              >
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    setExportMode("all");
                    setFromDate("");
                    setToDate("");
                    setShowExportModal(true);
                  }}
                >
                  <i className="fa-solid fa-file-export" style={{ marginRight: 8 }} />
                  Export
                </button>
              </div>
            ) : null}
          </div>

          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div className="role-table-top">
                  <form onSubmit={applySearch} className="role-table-search-wrap">
                    <input
                      type="text"
                      className="form-control role-table-search"
                      placeholder={listingOwners ? "Search owners..." : "Search tenants..."}
                      value={searchInput}
                      onChange={handleSearchChange}
                    />
                  </form>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>
                          <span className="role-th">
                            Sl&nbsp;No.
                          </span>
                        </th>
                        <th>
                          <span className="role-th">
                            Name
                          </span>
                        </th>
                        <th>
                          <span className="role-th">
                            Email
                          </span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">
                            Mobile
                          </span>
                        </th>
                        {showOwnerColumns ? (
                          <>
                           <th style={{ width: 150 }}><span className="role-th">Owner name</span></th>
                           <th style={{ width: 200 }}><span className="role-th">Owner email</span></th>
                           </>
                          ) : null}
                        <th style={{ width: 100 }}>
                          <span className="role-th">
                            Flat&nbsp;No.
                          </span>
                        </th>
                        {showCorpusColumns ? (
                          <>
                            <th style={{ width: 150 }}>
                              <span className="role-th">Corpus fund </span>
                            </th>
                            <th style={{ width: 160 }}>
                              <span className="role-th">Corpus fund date</span>
                            </th>
                          </>
                        ) : null}
                           <th style={{ width: 150 }}>
                          <span className="role-th">Property nature</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">
                            Status
                          </span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleTenants.length ? (
                        visibleTenants.map((tenant, idx) => (
                          <tr key={getId(tenant) || idx}>
                            <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                            <td>{tenant?.name || "-"}</td>
                            <td>{tenant?.email || "-"}</td>
                            <td>{tenant?.phone || "-"}</td>
                             {showOwnerColumns ? (
                              <>
                               <td>{tenant?.ownerName || "-"}</td>
                               <td>{tenant?.ownerEmail || "-"}</td>
                               </>
                               ) : null}
                            <td>{tenant?.flatNumber || "-"}</td>
                                                       {showCorpusColumns ? (
                              <>
                                <td>
                                  {tenant?.corpusFundAmount != null && tenant?.corpusFundAmount !== ""
                                    ? new Intl.NumberFormat("en-IN", {
                                        style: "currency",
                                        currency: "INR",
                                        maximumFractionDigits: 0,
                                      }).format(Number(tenant.corpusFundAmount))
                                    : "-"}
                                </td>
                                <td>
                                  {tenant?.corpusFundPaidDate
                                    ? new Date(tenant.corpusFundPaidDate).toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                      })
                                    : "-"}
                                </td>
                              </>
                            ) : null}
                            <td>{tenant?.propertyNature || "-"}</td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  padding: "0.35rem 0.65rem",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  fontSize: 11,
                                  background: tenant?.isActive ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)",
                                  color: tenant?.isActive ? "#16a34a" : "#dc2626",
                                }}
                              >
                                {tenant?.isActive ? "ACTIVE" : "INACTIVE"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex" style={{ gap: 8 }}>
                                <button
                                  type="button"
                                  className="role-action-btn role-action-view"
                                  title="View"
                                  onClick={() => handleView(tenant)}
                                >
                                  <i className="fa-solid fa-eye" />
                                </button>
                                {/* <button
                                  type="button"
                                  className="role-action-btn role-action-edit"
                                  title="Edit"
                                  onClick={() => handleEdit(tenant)}
                                >
                                  <i className="fa-solid fa-pen" />
                                </button>
                                <button
                                  type="button"
                                  className="role-action-btn role-action-delete"
                                  title="Delete"
                                  onClick={() => handleDelete(tenant)}
                                >
                                  <i className="fa-solid fa-trash" />
                                </button> */}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={showOwnerColumns ? 11 : 11} className="text-center text-muted">
                            {searchQuery
                              ? "No records found matching your search"
                              : roleFilter === "OWNER"
                                ? "No owners"
                                : "No tenants"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="role-table-footer">
                  <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 10 }}>
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Show:
                      </span>
                      <select
                        className="form-control"
                        style={{ width: 90 }}
                        value={pagination.limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        of {formatNumber(totalTenants)} entries
                      </span>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: 4 }}>
                      {/* First page button */}
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(1)}
                        style={{ minWidth: 32, height: 32, padding: '4px 8px', fontSize: 12 }}
                      >
                        &laquo;
                      </button>
                      
                      {/* Previous page button */}
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                        style={{ minWidth: 32, height: 32, padding: '4px 8px', fontSize: 12 }}
                      >
                        &lsaquo;
                      </button>

                      {/* Dynamic page numbers */}
                      {pageNumbers.map((pageNum, index) => {
                        if (pageNum === '...') {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              style={{ 
                                padding: '4px 8px', 
                                fontSize: 12, 
                                color: '#6c757d',
                                minWidth: 32,
                                textAlign: 'center',
                                display: 'inline-block'
                              }}
                            >
                              ...
                            </span>
                          );
                        }
                        
                        const isActive = pageNum === pagination.page;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            className={`btn ${isActive ? 'btn-primary' : 'btn-light'}`}
                            onClick={() => handlePageChange(pageNum)}
                            style={{ 
                              minWidth: 32, 
                              height: 32, 
                              padding: '4px 8px', 
                              fontSize: 12,
                              fontWeight: isActive ? 600 : 400
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      {/* Next page button */}
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                        style={{ minWidth: 32, height: 32, padding: '4px 8px', fontSize: 12 }}
                      >
                        &rsaquo;
                      </button>
                      
                      {/* Last page button */}
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => handlePageChange(pagination.pages)}
                        style={{ minWidth: 32, height: 32, padding: '4px 8px', fontSize: 12 }}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
         {showExportModal ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: 24,
          }}
          onClick={() => setShowExportModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              width: 500,
              maxWidth: "94%",
              boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}
            >
              <h5 className="m-0" style={{ fontWeight: 500 }}>Export Owner Data</h5>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setShowExportModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 26,
                  lineHeight: 1,
                  color: "#6b7280",
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Export Type</div>

              <div className="d-flex align-items-center" style={{ gap: 24, marginBottom: 20 }}>
                <label className="d-flex align-items-center m-0" style={{ gap: 8, fontWeight: 700, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="exportType"
                    checked={exportMode === "all"}
                    onChange={() => setExportMode("all")}
                  />
                  All
                </label>
                <label className="d-flex align-items-center m-0" style={{ gap: 8, fontWeight: 700, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="exportType"
                    checked={exportMode === "date"}
                    onChange={() => setExportMode("date")}
                  />
                  Filter by date
                </label>
              </div>

              {exportMode === "date" ? (
                <div className="row">
                  <div className="col-12 col-md-6">
                    <label style={{ fontWeight: 700, marginBottom: 6 }}>From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      max={toDate || undefined}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={{ fontWeight: 700, marginBottom: 6 }}>To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      min={fromDate || undefined}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div
              className="d-flex justify-content-end"
              style={{ gap: 10, padding: "14px 20px", borderTop: "1px solid #e5e7eb" }}
            >
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                style={{
                  background: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: 999,
                  padding: "8px 26px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDownloadReport}
                style={{
                  background: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: 999,
                  padding: "8px 30px",
                  fontWeight: 500,
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TenantReport;